package rendering

import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableLongStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameMillis
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.graphicsLayer
import domain.COLORS
import domain.pieces.Gravinome
import domain.player.GraphicsConfigStore
import kotlin.random.Random

// ── BgPiece ───────────────────────────────────────────────────────────────────

/**
 * A single falling background piece, built from a real [Gravinome] shape at a
 * random rotation.
 *
 * @param x        Current X position (pixels from left).
 * @param y        Current Y position (pixels from top).
 * @param speed    Fall speed in pixels per millisecond.
 * @param cellSize Size of each cell (pixels).
 * @param colorId  Piece type ID — used to look up the colour from [COLORS].
 * @param cells    Non-zero (row, col) offsets within the rotated shape.
 * @param cols     Width of the bounding box (for spawn X calculation).
 * @param rows     Height of the bounding box (for culling).
 * @param alpha    Opacity [0, 1].
 */
data class BgPiece(
    var x: Float,
    var y: Float,
    val speed: Float,
    val cellSize: Float,
    val colorId: Int,
    val cells: List<Pair<Int, Int>>,
    val cols: Int,
    val rows: Int,
    val alpha: Float,
)

// ── BackgroundState ───────────────────────────────────────────────────────────

/**
 * Pure-Kotlin state for the animated falling-pieces background.
 *
 * Uses the full [Gravinome.definedPieces] collection (trominoes, tetrominoes,
 * pentominoes) at random rotations (0–3).
 */
class BackgroundState {

    private val _pieces = mutableListOf<BgPiece>()
    private var spawnTimer = 0f
    private val spawnInterval = 800f

    // Pre-build a pool of (colorId, cells, cols, rows) for all pieces × 4 rotations
    private data class PieceVariant(
        val colorId: Int,
        val cells: List<Pair<Int, Int>>,
        val cols: Int,
        val rows: Int,
    )

    private val variants: List<PieceVariant> = buildVariants()

    private fun buildVariants(): List<PieceVariant> {
        val result = mutableListOf<PieceVariant>()
        for (piece in Gravinome.definedPieces) {
            for (rotation in 0..3) {
                val matrix = piece.getRotationsState(rotation)
                val cells = mutableListOf<Pair<Int, Int>>()
                for (r in 0 until matrix.rows) {
                    for (c in 0 until matrix.cols) {
                        if (matrix[r, c] != 0) cells += r to c
                    }
                }
                if (cells.isEmpty()) continue
                result += PieceVariant(
                    colorId = piece.id,
                    cells   = cells,
                    cols    = matrix.cols,
                    rows    = matrix.rows,
                )
            }
        }
        return result
    }

    fun update(deltaMs: Float, width: Float, height: Float) {
        val rng = Random.Default

        // Move pieces down, cull off-screen
        val iter = _pieces.iterator()
        while (iter.hasNext()) {
            val p = iter.next()
            p.y += p.speed * deltaMs
            if (p.y > height + p.rows * p.cellSize) iter.remove()
        }

        // Spawn on timer
        spawnTimer += deltaMs
        if (spawnTimer >= spawnInterval) {
            spawnTimer -= spawnInterval
            val v = variants[rng.nextInt(variants.size)]
            val cellSize = rng.nextFloat() * 14f + 8f  // 8–22 px
            val pieceWidthPx = v.cols * cellSize
            _pieces += BgPiece(
                x        = rng.nextFloat() * (width - pieceWidthPx).coerceAtLeast(0f),
                y        = -(v.rows * cellSize),
                speed    = rng.nextFloat() * 0.04f + 0.01f,  // 0.01–0.05 px/ms
                cellSize = cellSize,
                colorId  = v.colorId,
                cells    = v.cells,
                cols     = v.cols,
                rows     = v.rows,
                alpha    = rng.nextFloat() * 0.18f + 0.04f,  // 0.04–0.22
            )
        }
    }

    internal fun pieces(): List<BgPiece> = _pieces
}

// ── LocalBackgroundState ──────────────────────────────────────────────────────

/**
 * CompositionLocal providing a single [BackgroundState] for the entire app
 * lifetime. Hoisted in [GravitarApp] so scene transitions don't reset it.
 */
val LocalBackgroundState = compositionLocalOf { BackgroundState() }

// ── AnimatedBackground ────────────────────────────────────────────────────────

@Composable
fun AnimatedBackground(modifier: Modifier = Modifier) {
    val bgState = LocalBackgroundState.current
    val graphicsConfig = remember { GraphicsConfigStore.load() }
    val opacity = graphicsConfig.backgroundOpacity.toFloat().coerceIn(0f, 1f)

    if (opacity <= 0f) return

    var frameToken by remember { mutableLongStateOf(0L) }

    LaunchedEffect(Unit) {
        var lastTime = withFrameMillis { it }
        while (true) {
            val now = withFrameMillis { it }
            lastTime = now
            frameToken = now
            @Suppress("UNUSED_VARIABLE")
            val unused = lastTime
        }
    }

    Canvas(modifier = modifier.graphicsLayer { alpha = opacity }) {
        @Suppress("UNUSED_EXPRESSION")
        frameToken

        val w = size.width
        val h = size.height

        bgState.update(16f, w, h)

        // 1. Background fill
        drawRect(color = Color(0xFF_08_08_12.toInt()), topLeft = Offset.Zero, size = size)

        // 2. Scanlines
        drawScanlines(w, h)

        // 3. Grid
        drawBackgroundGrid(w, h)

        // 4. Falling pieces
        drawBgPieces(bgState)
    }
}

// ── DrawScope helpers ─────────────────────────────────────────────────────────

private fun DrawScope.drawScanlines(w: Float, h: Float) {
    val lineSpacing = 4f
    var y = 0f
    while (y < h) {
        drawLine(color = Color(0f, 0f, 0f, 0.18f), start = Offset(0f, y), end = Offset(w, y), strokeWidth = 1f)
        y += lineSpacing
    }
}

private fun DrawScope.drawBackgroundGrid(w: Float, h: Float) {
    val gridSize = 40f
    val gridColor = Color(1f, 1f, 1f, 0.025f)
    var x = 0f
    while (x <= w) {
        drawLine(color = gridColor, start = Offset(x, 0f), end = Offset(x, h), strokeWidth = 0.5f)
        x += gridSize
    }
    var y = 0f
    while (y <= h) {
        drawLine(color = gridColor, start = Offset(0f, y), end = Offset(w, y), strokeWidth = 0.5f)
        y += gridSize
    }
}

private fun DrawScope.drawBgPieces(state: BackgroundState) {
    for (piece in state.pieces()) {
        val hex = COLORS[piece.colorId] ?: 0x888888
        val r = ((hex shr 16) and 0xFF) / 255f
        val g = ((hex shr 8)  and 0xFF) / 255f
        val b = (hex and 0xFF) / 255f
        val color = Color(r, g, b, piece.alpha)

        for ((row, col) in piece.cells) {
            val cx = piece.x + col * piece.cellSize
            val cy = piece.y + row * piece.cellSize
            drawRect(
                color    = color,
                topLeft  = Offset(cx, cy),
                size     = Size(piece.cellSize - 1f, piece.cellSize - 1f),
            )
        }
    }
}
